import { clsx } from 'clsx';
import { useState, useRef, useEffect } from 'react';

import type { KeyboardEvent, MouseEvent } from 'react';

import styles from './CopyableText.module.css';

import { Icon } from 'components/Icon';

export interface CopyableTextProps {
  /** The text to be copied */
  text: string;
  /** Additional class name to style the text so it suits the design */
  additionalClassName?: string;
  /** ARIA label for the copy button */
  ariaLabelCopy?: string;
  /** ARIA label for the copied state */
  ariaLabelHasBeenCopied?: string;
  /** custom value to be copied in place of the display text */
  customCopyValue?: string;
}

const COPYTIME = 2000;

export const CopyableText = ({
  text,
  additionalClassName = '',
  ariaLabelCopy,
  ariaLabelHasBeenCopied,
  customCopyValue,
}: CopyableTextProps) => {
  const [isCopying, setIsCopying] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [buttonActive, setButtonActive] = useState(false);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleCopy = (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLSpanElement>) => {
    // prevent event bubbling if placed inside another clikable element
    event.stopPropagation();
    event.preventDefault();

    setIsCopying(true);
    timeoutRef.current = setTimeout(() => {
      setIsCopying(false);
      setButtonActive(false);
    }, COPYTIME);
    navigator.clipboard.writeText(customCopyValue ?? text).catch(() => setIsCopying(false));
  };

  return (
    <span
      className={clsx(styles['copyable-text-container'], additionalClassName)}
      role="button"
      tabIndex={0}
      onFocus={() => setButtonActive(true)}
      onBlur={() => !isCopying && setButtonActive(false)}
      onKeyDown={(event) => {
        setButtonActive(true);
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleCopy(event);
        }
      }}
      onMouseEnter={() => setButtonActive(true)}
      onMouseLeave={() => !isCopying && setButtonActive(false)}
    >
      {text}
      {buttonActive && (
        <button
          ref={buttonRef}
          className={clsx(styles['copyable-text-copy-button'], additionalClassName)}
          onClick={handleCopy}
          type="button"
          aria-keyshortcuts="Enter Space"
          aria-label={isCopying ? (ariaLabelHasBeenCopied ?? 'Copied') : (ariaLabelCopy ?? 'Copy to clipboard')}
          tabIndex={-1}
        >
          {text}
          <Icon name={isCopying ? 'CheckSmall' : 'Copy'} size={20} />
          <span className={styles['copyable-text-copy-visually-hidden']} aria-live="polite">
            {isCopying ? (ariaLabelHasBeenCopied ?? 'Copied') : (ariaLabelCopy ?? 'Copy to clipboard')}
          </span>
        </button>
      )}
    </span>
  );
};
